import { useEffect, useMemo, useState } from 'react'
import { Accessibility, Activity, Baby, Bed, Brain, Building, Building2, Droplets, Heart, HeartPulse, Home, Hospital, List, Pill, RefreshCw, Search, Shield, Smile, Stethoscope, Sun, Users } from 'lucide-react'
import BSDCard from '../BSDCard'
import AutoGrid from '../AutoGrid'
import DistrictMap from '../DistrictMap'
import { AMBITI_STATS, type AmbitoId } from '../../data/ambitiStats'
import { STRUTTURE_SANITARIE, strutturaAsstSlug } from '../../data/struttureSanitarie'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LabelList } from 'recharts'
import { CHART_TOOLTIP_STYLE, AMBITO_COMPARE_COLORS, formatInt } from '../../utils/dashboardFormatters'
import CompareAmbitiBarCard from '../shared/CompareAmbitiBarCard'
import { Link } from 'react-router-dom'
import StruttureSanitarieDialogList from './StruttureSanitarieDialogList'

const BSD_PALETTE = ['#0891b2', '#0ea5e9', '#0284c7', '#06b6d4', '#10b981', '#f59e0b', '#8b5cf6']

const BSD_ICONS: Record<string, React.ComponentType<{ size?: number | string; style?: React.CSSProperties }>> = {
  'Neuropsichiatria Infantile': Brain,
  'Consultorio Familiare': Home,
  'Poliambulatorio': Stethoscope,
  'Ambulatorio': Activity,
  'Ospedale': Hospital,
  'Centro Psico-Sociale': HeartPulse,
  'Centro Assistenza per Dialisi': Droplets,
  'Servizio Tossicodipendenze': Shield,
  'Ambulatorio Alcologia': Pill,
  'Odontoiatria': Smile,
  'Centro Diurno Psichiatrico': Sun,
  'Centro Diurno Neuropsichiatria': Baby,
  'Centro Diurno Disabili': Accessibility,
  'Ambulatorio Psichiatrico': Brain,
  'Presidio Residenziale Psichiatrico': Building,
  'Comunità Protetta': Shield,
  'Comunità Psichiatrica': Users,
  'Comunità Riabilitativa': RefreshCw,
  'Hospice': Heart,
  'Residenza Sanitaria Disabili': Bed,
}

const GLOBAL_BSD_ORDER: string[] = [
  // Riga 1 — servizi territoriali principali + ospedale
  'Ospedale',
  'Poliambulatorio',
  'Ambulatorio',
  'Consultorio Familiare',
  'Centro Psico-Sociale',
  'Centro Assistenza per Dialisi',
  'Servizio Tossicodipendenze',
  'Ambulatorio Alcologia',
  'Neuropsichiatria Infantile',
  'Odontoiatria',
  'Centro Diurno Psichiatrico',
  'Centro Diurno Neuropsichiatria',
  'Centro Diurno Disabili',
  'Ambulatorio Psichiatrico',
  'Presidio Residenziale Psichiatrico',
  'Comunità Protetta',
  'Comunità Psichiatrica',
  'Comunità Riabilitativa',
  'Residenza Sanitaria Disabili',
  'Hospice',
]

const BSD_DESCRIPTIONS: Record<string, string> = {
  'Neuropsichiatria Infantile': 'Servizi di diagnosi e cura dei disturbi neurologici e psichiatrici in età evolutiva.',
  'Consultorio Familiare': 'Strutture di supporto alla famiglia per salute sessuale, riproduttiva e psicosociale.',
  'Poliambulatorio': 'Presidi con più ambulatori specialistici per visite, esami e prestazioni diagnostiche.',
  'Ambulatorio': 'Sedi ambulatoriali per visite mediche specialistiche e prestazioni sanitarie.',
  'Ospedale': 'Presidi ospedalieri per ricoveri, pronto soccorso e interventi chirurgici.',
  'Centro Psico-Sociale': 'Servizi territoriali di salute mentale per adulti con disturbi psichici.',
  'Centro Assistenza per Dialisi': 'Centri per il trattamento dialitico dei pazienti con insufficienza renale.',
  'Servizio Tossicodipendenze': 'Servizi per la prevenzione, diagnosi e cura delle dipendenze patologiche.',
  'Ambulatorio Alcologia': 'Ambulatori dedicati alla diagnosi e al trattamento dei disturbi legati all\'alcol.',
  'Odontoiatria': 'Servizi di cure odontoiatriche e igiene dentale sul territorio.',
  'Centro Diurno Psichiatrico': 'Strutture diurne per percorsi riabilitativi e di socializzazione per pazienti psichiatrici.',
  'Centro Diurno Neuropsichiatria': 'Centri diurni per attività riabilitative rivolte a minori con disturbi neuropsichiatrici.',
  'Centro Diurno Disabili': 'Strutture diurne che offrono attività educative e riabilitative per persone con disabilità.',
  'Ambulatorio Psichiatrico': 'Ambulatori per visite, colloqui e terapie dedicate alla salute mentale adulta.',
  'Presidio Residenziale Psichiatrico': 'Strutture residenziali per pazienti psichiatrici con necessità di assistenza continuativa.',
  'Comunità Protetta': 'Comunità residenziali a bassa intensità assistenziale per persone con fragilità psichica.',
  'Comunità Psichiatrica': 'Strutture comunitarie per percorsi terapeutici e riabilitativi in ambito psichiatrico.',
  'Comunità Riabilitativa': 'Comunità dedicate alla riabilitazione socio-sanitaria di pazienti con disabilità o disagio.',
  'Hospice': 'Strutture per le cure palliative e l\'assistenza ai pazienti in fase terminale.',
  'Residenza Sanitaria Disabili': 'Residenze per persone con disabilità grave che necessitano di assistenza continuativa.',
}

const AZIONI_AXIS_LABEL_MAX_CHARS = 12
const AZIONI_AXIS_LABEL_WIDTH = 80

function wrapAxisLabel(value: string, maxChars = AZIONI_AXIS_LABEL_MAX_CHARS): string[] {
  const clean = value.trim()
  if (!clean) return ['']

  const words = clean.split(/\s+/)
  const lines: string[] = []
  let currentLine = ''

  for (const word of words) {
    if (!currentLine) {
      currentLine = word
      continue
    }

    if (`${currentLine} ${word}`.length <= maxChars) {
      currentLine = `${currentLine} ${word}`
    } else {
      lines.push(currentLine)
      currentLine = word
    }
  }

  if (currentLine) lines.push(currentLine)
  return lines
}

function StruttureBarTooltip({
  active,
  payload,
}: {
  active?: boolean
  payload?: Array<{ value?: number; payload?: { name?: string; value?: number } }>
}) {
  if (!active || !payload || payload.length === 0) return null

  const point = payload[0]
  const nome = point?.payload?.name ?? 'Tipologia'
  const valore = point?.payload?.value ?? point?.value ?? 0

  return (
    <div
      style={{
        background: 'var(--tooltip-bg)',
        border: '1px solid var(--tooltip-border)',
        borderRadius: 8,
        fontSize: 12,
        color: 'var(--tooltip-text)',
        padding: '8px 10px',
      }}
    >
      <div style={{ color: 'var(--text-0)', fontWeight: 600, marginBottom: 2 }}>{nome}</div>
      <div style={{ color: 'var(--text-2)' }}>{`Strutture: ${formatInt(Number(valore))}`}</div>
    </div>
  )
}


function LeftAlignedAxisTick({
  y = 0,
  payload,
}: {
  y?: number
  payload?: { value?: string | number }
}) {
  const lines = wrapAxisLabel(String(payload?.value ?? ''))

  return (
    <text x={0} y={y} fill="var(--text-2)" fontSize={10} textAnchor="start">
      {lines.map((line, index) => (
        <tspan key={`${line}-${index}`} x={0} dy={index === 0 ? 4 : 11}>
          {line}
        </tspan>
      ))}
    </text>
  )
}

export default function StruttureSanitarieSection({
  selectedAmbitoId,
  onSelectedAmbitoChange,
}: {
  selectedAmbitoId: AmbitoId | null
  onSelectedAmbitoChange: (ambitoId: AmbitoId | null) => void
}) {
  const [selectedStrutturaSanitariaId, setSelectedStrutturaSanitariaId] = useState<string | null>(null)
  const [selectedStrutturaDialogId, setSelectedStrutturaDialogId] = useState<string | null>(null)

  const struttureSanitarieData = useMemo(() => {
    const scopeAll = selectedAmbitoId
      ? STRUTTURE_SANITARIE.filter(struttura => struttura.ambitoId === selectedAmbitoId)
      : STRUTTURE_SANITARIE

    const perElenco = [...scopeAll].sort((a, b) => a.nome.localeCompare(b.nome, 'it'))
    const palette = BSD_PALETTE
    const globalTipoOrder = Array.from(
      STRUTTURE_SANITARIE.reduce((map, struttura) => {
        map.set(struttura.tipo, (map.get(struttura.tipo) ?? 0) + 1)
        return map
      }, new Map<string, number>()).entries(),
    )
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], 'it'))
      .map(([tipo]) => tipo)

    const perTipo = globalTipoOrder.map((tipo, index) => ({
      id: `${tipo}-${index}`.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
      name: tipo,
      value: perElenco.filter(struttura => struttura.tipo === tipo).length,
      color: palette[index % palette.length],
    }))

    const globalBsdOrder = Array.from(
      STRUTTURE_SANITARIE.reduce((map, struttura) => {
        const key = struttura.bsdName || 'Altro'
        map.set(key, (map.get(key) ?? 0) + 1)
        return map
      }, new Map<string, number>()).entries(),
    )
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], 'it'))
      .map(([name]) => name)

    const perBsdName = globalBsdOrder.map((name, index) => ({
      id: `bsd-${name}-${index}`.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
      name,
      value: perElenco.filter(struttura => (struttura.bsdName || 'Altro') === name).length,
      color: palette[index % palette.length],
    })).filter(entry => entry.value > 0)

    return {
      totale: perElenco.length,
      tipologie: perTipo.length,
      perElenco,
      perTipo,
      perBsdName,
      markers: perElenco.map(struttura => ({
        key: struttura.id,
        nome: struttura.nome,
        ambitoId: struttura.ambitoId,
        lng: struttura.longitudine,
        lat: struttura.latitudine,
      })),
    }
  }, [selectedAmbitoId])

  const normalizeTipoLabel = (value: string) => value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()

  const presidioOspedalieroCard = useMemo(
    () => struttureSanitarieData.perTipo.find(tipo => normalizeTipoLabel(tipo.name).includes('presidio ospedaliero')),
    [struttureSanitarieData.perTipo],
  )

  const strutturePerTipoSenzaPresidio = useMemo(
    () => struttureSanitarieData.perTipo.filter(tipo => !normalizeTipoLabel(tipo.name).includes('presidio ospedaliero')),
    [struttureSanitarieData.perTipo],
  )

  const consultoriList = useMemo(
    () => struttureSanitarieData.perElenco.filter(struttura => normalizeTipoLabel(`${struttura.tipo} ${struttura.nome}`).includes('consultorio')),
    [struttureSanitarieData.perElenco],
  )

  const strutturePerCategoria = useMemo(() => {
    const groups = new Map<string, typeof struttureSanitarieData.perElenco>()
    for (const struttura of struttureSanitarieData.perElenco) {
      const key = struttura.bsdName || 'Altro'
      if (!groups.has(key)) groups.set(key, [])
      groups.get(key)!.push(struttura)
    }
    return GLOBAL_BSD_ORDER.map(bsdName => ({
      bsdName,
      strutture: groups.has(bsdName)
        ? [...groups.get(bsdName)!].sort((a, b) => a.nome.localeCompare(b.nome, 'it'))
        : [],
    }))
  }, [struttureSanitarieData.perElenco])

  const struttureCompareData = useMemo(() => {
    return GLOBAL_BSD_ORDER.map(bsdName => ({
      bsdName,
      points: AMBITI_STATS.map(ambito => ({
        id: ambito.id,
        name: ambito.nome,
        value: STRUTTURE_SANITARIE.filter(s => s.ambitoId === ambito.id && (s.bsdName || 'Altro') === bsdName).length,
        color: AMBITO_COMPARE_COLORS[ambito.id],
      })),
    }))
  }, [])

  useEffect(() => {
    if (!selectedStrutturaSanitariaId) return
    const existsInScope = struttureSanitarieData.perElenco.some(struttura => struttura.id === selectedStrutturaSanitariaId)
    if (!existsInScope) setSelectedStrutturaSanitariaId(null)
  }, [selectedStrutturaSanitariaId, struttureSanitarieData.perElenco])

  useEffect(() => {
    if (!selectedStrutturaDialogId) return
    const existsInScope = struttureSanitarieData.perElenco.some(struttura => struttura.id === selectedStrutturaDialogId)
    if (!existsInScope) setSelectedStrutturaDialogId(null)
  }, [selectedStrutturaDialogId, struttureSanitarieData.perElenco])

  return (
    <>
      <h3 className="telemetry-section-title" style={{ marginBottom: 8 }}>Strutture Sanitarie</h3>

      <AutoGrid style={{ marginBottom: 14 }}>
       

        <BSDCard className="telemetry-map-card telemetry-cdc-map-card" kind="panel" sezione="Strutture Sanitarie" style={{ minHeight: 120, padding: 10 }} zoomable>
          <h3 className="telemetry-section-title" style={{ marginTop: 0, marginBottom: 8 }}>Seleziona l'ambito</h3>
          <DistrictMap
            onSelectDistrict={(id) => {
              onSelectedAmbitoChange(id as AmbitoId)
              setSelectedStrutturaSanitariaId(null)
            }}
            onClearSelection={() => {
              onSelectedAmbitoChange(null)
              setSelectedStrutturaSanitariaId(null)
            }}
            selectedDistrictId={selectedAmbitoId ?? null}
            showLegend={false}
            showInstructionText={false}
            sanitaryMarkers={struttureSanitarieData.markers}
            selectedSanitaryKey={selectedStrutturaSanitariaId}
            onSelectSanitary={(id) => {
              const selected = STRUTTURE_SANITARIE.find(struttura => struttura.id === id)
              if (selected) onSelectedAmbitoChange(selected.ambitoId)
              setSelectedStrutturaSanitariaId(id)
              setTimeout(() => {
                const el = document.querySelector('.telemetry-strutture-list-body')
                if (el) el.scrollTop = 0
              }, 50)
            }}
          />
        </BSDCard>



        <BSDCard
          kind="kpi"
          sezione="Strutture Sanitarie"
          label="Strutture Sanitarie"
          value={formatInt(struttureSanitarieData.perElenco.length)}
          zoomable
          hideZoomButton
          sub={
            <p style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.6, margin: 0 }}>
              Le strutture sanitarie sono i presidi e le sedi in cui vengono erogati servizi di prevenzione, diagnosi, cura e riabilitazione sul territorio della Provincia di Monza e Brianza.
            </p>
          }
          zoomSub={
            <StruttureSanitarieDialogList
              strutture={struttureSanitarieData.perElenco}
              onSelect={(id) => {
                setSelectedStrutturaSanitariaId(id)
                setSelectedStrutturaDialogId(id)
              }}
            />
          }
          footer={(openZoom) => (
            <button
              type="button"
              onClick={openZoom}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                width: '100%',
                padding: '6px 0',
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--accent)',
                background: 'none',
                border: 'none',
                borderTop: '1px solid var(--border)',
                cursor: 'pointer',
                justifyContent: 'center',
                marginTop: 8,
                paddingTop: 8,
              }}
            >
              <List size={14} />
              Vedi elenco completo
            </button>
          )}
        />

        <BSDCard kind="panel" zoomable sezione="Strutture Sanitarie" style={{ display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
          <h3 className="telemetry-section-title" style={{ marginTop: 0, marginBottom: 8 }}>Tipologia</h3>
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', paddingRight: 4 }}>
            <ResponsiveContainer width="100%" height={Math.max(220, struttureSanitarieData.perBsdName.length * 34)}>
              <BarChart data={struttureSanitarieData.perBsdName} layout="vertical" margin={{ top: 4, right: 22, bottom: 4, left: 6 }}>
                <XAxis type="number" hide allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={<LeftAlignedAxisTick />} axisLine={false} tickLine={false} width={AZIONI_AXIS_LABEL_WIDTH} />
                <Tooltip content={<StruttureBarTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
                <Bar dataKey="value" radius={[0, 6, 6, 0]} barSize={14}>
                  <LabelList dataKey="value" position="right" formatter={value => formatInt(Number(value ?? 0))} fill="var(--text-2)" fontSize={11} />
                  {struttureSanitarieData.perBsdName.map((entry, idx) => (
                    <Cell key={`strutture-bsd-${entry.id}-${idx}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </BSDCard>
     
        {strutturePerCategoria.map(({ bsdName, strutture }) => {
          const Icon = BSD_ICONS[bsdName]
          const col = struttureSanitarieData.perBsdName.find(e => e.name === bsdName)?.color
            ?? BSD_PALETTE[GLOBAL_BSD_ORDER.indexOf(bsdName) % BSD_PALETTE.length]
          return (
            <BSDCard
              key={bsdName}
              kind="kpi"
              sezione="Strutture Sanitarie"
              label={bsdName}
              value={formatInt(strutture.length)}
              icon={Icon}
              color={col}
              zoomable
              hideZoomButton
              sub={
                <p style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.6, margin: 0 }}>
                  {BSD_DESCRIPTIONS[bsdName] ?? `Strutture di tipo ${bsdName} presenti sul territorio.`}
                </p>
              }
              zoomSub={
                <StruttureSanitarieDialogList
                  strutture={strutture}
                  onSelect={(id) => {
                    setSelectedStrutturaSanitariaId(id)
                    setSelectedStrutturaDialogId(id)
                  }}
                />
              }
              footer={(openZoom) => (
                <button
                  type="button"
                  onClick={openZoom}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    width: '100%',
                    padding: '6px 0',
                    fontSize: 11,
                    fontWeight: 600,
                    color: col,
                    background: 'none',
                    border: 'none',
                    borderTop: '1px solid var(--border)',
                    cursor: 'pointer',
                    justifyContent: 'center',
                    marginTop: 8,
                    paddingTop: 8,
                  }}
                >
                  <List size={14} />
                  Vedi elenco completo
                </button>
              )}
            />
          )
        })}
      </AutoGrid>

      {selectedAmbitoId === null && (
        <>
          <h3 className="telemetry-section-title" style={{ marginTop: 24, marginBottom: 8 }}>Comparazione tra ambiti</h3>
          <AutoGrid style={{ marginBottom: 14 }}>
            {struttureCompareData.map(({ bsdName, points }) => (
              <CompareAmbitiBarCard
                key={bsdName}
                title={bsdName}
                data={points}
                formatter={formatInt}
                valueLabel="Strutture"
                sezione="Strutture Sanitarie"
              />
            ))}
          </AutoGrid>
        </>
      )}
    </>
  )
}


